import { StorageService } from './storage.service';
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import {
  CapacitorSQLite,
  SQLiteConnection,
  SQLiteDBConnection,
} from '@capacitor-community/sqlite';
import { isNil, startsWith } from '@qld-recreational/ramda';

@Injectable({
  providedIn: 'root',
})
export class NativeStorageService extends StorageService {
  private readonly DB_NAME = 'app_db';
  private readonly sqlite = new SQLiteConnection(CapacitorSQLite);
  private db: SQLiteDBConnection | null = null;
  private initPromise: Promise<void> | null = null;

  constructor(private platform: Platform) {
    super();
  }

  async init() {
    await this.platform.ready();

    if (!isNil(this.db)) {
      return;
    }

    if (!isNil(this.initPromise)) {
      return this.initPromise;
    }

    this.initPromise = this.initializeDb();

    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  private async initializeDb() {
    let connection: SQLiteDBConnection | null = null;

    try {
      const hasExistingConnection = (
        await this.sqlite.isConnection(this.DB_NAME, false)
      ).result;

      connection = hasExistingConnection
        ? await this.sqlite.retrieveConnection(this.DB_NAME, false)
        : await this.sqlite.createConnection(
            this.DB_NAME,
            false,
            'no-encryption',
            1,
            false,
          );

      await connection.open();
      console.log('Database opened successfully');
    } catch (error) {
      this.db = null;
      console.error('Error opening database:', error);
      throw error;
    }

    const createTableSQL = `
      CREATE TABLE IF NOT EXISTS storage (
        key TEXT PRIMARY KEY NOT NULL,
        value TEXT
      );
    `;
    await connection.execute(createTableSQL);
    this.db = connection;
  }

  async set(key: string, value: any) {
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    const valStr = JSON.stringify(value);
    await this.db.run(`REPLACE INTO storage (key, value) VALUES (?, ?)`, [
      key,
      valStr,
    ]);
  }

  async get(key: string): Promise<any> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    const res = await this.db.query(`SELECT value FROM storage WHERE key = ?`, [
      key,
    ]);
    if (res.values?.length) {
      return JSON.parse(res.values[0].value);
    }
    return null;
  }

  async remove(key: string): Promise<any> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    await this.db.run(`DELETE FROM storage WHERE key = ?`, [key]);
  }

  async clear() {
    if (!this.db) throw new Error('Database not initialized');
    await this.db.run(`DELETE FROM storage`);
  }

  async keys() {
    if (!this.db) throw new Error('Database not initialized');
    const res = await this.db.query(`SELECT key FROM storage`);

    if (res.values?.length) {
      return res.values.map((row) => row.key);
    }
    return [];
  }

  async fetchState() {
    if (!this.db) throw new Error('Database not initialized');
    const state = {};
    const keys = await this.keys();

    await Promise.allSettled(
      keys.filter(startsWith(this.STORAGE_KEY)).map(async (key) => {
        const value = await this.get(key);
        state[key.replace(this.STORAGE_KEY + '-', '')] =
          typeof value === 'string'
            ? value.startsWith('{') || value.startsWith('[')
              ? JSON.parse(value)
              : value
            : value;
      }),
    );
    return state;
  }
}
